import { Component, Input, OnInit } from '@angular/core';
import { PageService } from '../../../../services/page.service';
import { PageChangedEvent } from '../../../../utils/events/page-changed-event';
import { environment } from '../../../../../environments/environment';
import { CommunicationService } from '../../../../services/communication.service';
import { SidebarService } from '../../../../services/sidebar.service';
import { SearchResult } from '../../../../models/search-result';
import { Chapter } from '../../../../models/chapter';
import { ChapterService } from '../../../../services/chapter.service';
import { SearchService } from '../../../../services/search.service';
import { ApplicationStateService } from '../../../../services/application-state.service';
import { StripHtmlPipe } from '../../../../pipes/strip-html.pipe';

@Component({
    selector: 'search-item',
    templateUrl: './search-item.component.html',
    styleUrls: ['./search-item.component.scss'],
    standalone: true,
    imports: [
        StripHtmlPipe
    ]
})
export class SearchItemComponent implements OnInit {

    @Input() searchResult: SearchResult;
    @Input() searchText: string;

    chapter: Chapter;
    nameToShow: string;

    hierarchy: Array<(number | string)>;
    private index: number;

    private preText = '';
    private resultText = '';
    private postText = '';

    constructor(private communicationService: CommunicationService,
                private sidebarService: SidebarService,
                private chapterService: ChapterService,
                private searchService: SearchService,
                private applicationStateService: ApplicationStateService,
                private pageService: PageService) {

        this.hierarchy = [];
        this.chapter = new Chapter();
        this.searchResult = new SearchResult();
    }

    ngOnInit(): void {
        this.index = this.pageService.getIndexByPageId(this.searchResult.pageId);

        this.chapterService.getChapterFromIndex(this.index).then(chapter => {
            this.chapter = chapter;

            this.getHierarchy();
        });
    }

    /**
     * get real page number of search result
     *
     * @returns {number}
     */
    getRealPageNumber(): number {
        return this.pageService.getRealPageNumber(this.pageService.getIndexByPageId(this.searchResult.pageId));
    }

    /**
     * turn to page of search result
     */
    turnToPage(): void {
        if (this.getRealPageNumber() < 0) {
            return;
        }

        const pageChangeEvent = new PageChangedEvent();
        pageChangeEvent.moveToPage = this.index;
        this.communicationService.announcePageTurn(pageChangeEvent);

        if (window.window.innerWidth <= (environment.mobileBreakPoint + 1)) {
            this.sidebarService.closeSidebar();
        }

        this.mouseOver();

        if (this.applicationStateService.isMobileMode()) {
            setTimeout(() => {
                this.mouseOut();
            }, 3000);
        }
    }

    /**
     * handle mouse over on search result (set active)
     */
    mouseOver(): void {
        this.searchService.setActive(this.searchResult);
    }

    /**
     * handle mouse out on search result (set active=false)
     */
    mouseOut(): void {
        this.searchService.clearActive();
    }

    /**
     * get text before found result
     *
     * @returns {string}
     */
    getPreText(): string {
        this.split();

        return this.preText;
    }

    /**
     * get search result text
     *
     * @returns {string}
     */
    getResultText(): string {
        this.split();

        return this.resultText;
    }

    /**
     * get text after found result
     *
     * @returns {string}
     */
    getPostText(): string {
        this.split();

        return this.postText;
    }

    /**
     * split search result text into result, text before result (preText) and text after (postText)
     */
    split(): void {
        if (this.searchText === '') {
            this.preText = this.searchResult.text;
            this.resultText = '';
            this.postText = '';

            return;
        }

        const query = this.modifyText(this.searchText).trim();
        const n = this.modifyText(this.searchResult.text).toLowerCase().indexOf(query.toLowerCase());

        if (n < 0) {
            this.preText = this.searchResult.text;
            this.resultText = '';
            this.postText = '';

            return;
        }

        this.preText = this.searchResult.text.substring(0, n);
        this.resultText = this.searchResult.text.substring(n, n + query.length);
        this.postText = this.searchResult.text.substring(n + query.length);

        this.shorten();
    }

    /**
     * Cut text before and after the result to max. 10 words.
     */
    private shorten(): void {
        const pre = this.preText.charAt(this.preText.length - 1) !== ' ';
        const post = this.postText.charAt(0) !== ' ';

        const preArray = this.modifyText(this.preText).split(' ');
        const postArray = this.modifyText(this.postText).split(' ');

        const preCount = preArray.length;
        const postCount = postArray.length;
        const words = 10;

        if (preCount + postCount <= words) {
            return;
        }

        let preMax = Math.min(words / 2, preCount);
        if (preCount < words / 2) {
            preMax = words - preCount;
        }

        let postMax = Math.min(words / 2, postCount);
        if (postCount < words / 2) {
            postMax = words - postCount;
        }

        let i;
        let text = '';
        for (i = 0; i < preMax; i++) {
            if (preArray.hasOwnProperty(preArray.length - preMax + i)) {
                text += `${preArray[preArray.length - preMax + i]} `;
            }
        }

        if (pre) {
            text = text.substr(0, text.length - 1);
        }
        this.preText = text;
        text = '';

        for (i = 0; i < postMax; i++) {
            if (postArray.hasOwnProperty(i)) {
                text += ` ${postArray[i]}`;
            }
        }

        if (post) {
            text = text.substr(1);
        }
        this.postText = text;
    }

    /**
     * Modify text to fit the sql LIKE operator
     * (replace ä, ö, ü etc.)
     *
     * @param {string} text
     * @returns {string}
     */
    private modifyText(text: string): string {
        return text.replace('ä', 'a')
            .replace('ö', 'o')
            .replace('ü', 'u')
            .replace('Ä', 'A')
            .replace('Ö', 'O')
            .replace('Ü', 'U');
    }

    /**
     * get chapter hierarchy of search result
     */
    private getHierarchy(): void {
        this.chapterService.getChapterHierarchyFromChapter(this.chapter).then(result => {
            this.hierarchy = result;
        });
    }
}
